/* Pixel Threads - Signal Sync
   The hero scan line and the emblem share one heartbeat.
   When the line crosses the creature's optical centre the emblem re-tunes:
   interior cells flip, the silhouette and the wordmark never move.
   Fires window event pt:signal-tune on the same frame so the rotating
   eyebrow line changes on the same beat. */
(function () {
  'use strict';

  var PASSES_PER_TUNE = 3;
  var VARIANT_COUNT = 4;
  var FLIP_RATIO = 0.30;
  var SEED = 20260814;

  if (!window.requestAnimationFrame) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var win, img, line, canvas, ctx, probe;
  var grid = null, variants = [], variantIndex = 0;
  var passes = 0, lastProgress = -1, targetProgress = 0.5;
  var rafId = 0, live = false;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function mulberry(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      var t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }

  ready(function () {
    win = document.querySelector('.hero-art-window');
    if (!win) return;
    img = win.querySelector('.hero-art-image');
    line = win.querySelector('.scan-line');
    if (!img || !line) return;

    canvas = document.createElement('canvas');
    canvas.className = 'pt-tune-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    win.appendChild(canvas);
    ctx = canvas.getContext('2d');
    stackBelowScanLine();

    probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      if (!analyse()) { canvas.remove(); return; }
      buildVariants();
      layout();
      watch();
    };
    probe.onerror = function () { canvas.remove(); };
    probe.src = img.currentSrc || img.src;

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(function () { layout(); paint(); resume(); }, 150);
    });
  });

  /* Keep the overlay above the RESOLVE canvas but under the scan line, 
     whatever z-indexes those two happen to carry. */
  function stackBelowScanLine() {
    var z = 2;
    var resolveCanvas = win.querySelector('.pt-resolve-canvas');
    if (resolveCanvas) {
      var rz = parseInt(window.getComputedStyle(resolveCanvas).zIndex, 10);
      if (!isNaN(rz)) z = rz;
    }
    var lz = parseInt(window.getComputedStyle(line).zIndex, 10);
    if (!isNaN(lz) && z >= lz) z = lz - 1;
    canvas.style.zIndex = String(z);
  }

  /* ---- read the emblem -------------------------------------------------- */

  function analyse() {
    var w = probe.naturalWidth, h = probe.naturalHeight;
    if (!w || !h) return false;
    var off = document.createElement('canvas');
    off.width = w; off.height = h;
    var octx = off.getContext('2d');
    octx.drawImage(probe, 0, 0);
    var data;
    try { data = octx.getImageData(0, 0, w, h).data; } catch (e) { return false; }

    var on = new Uint8Array(w * h);
    var y, x, r, c, p;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        p = (y * w + x) * 4;
        if (data[p + 3] < 32) continue;
        if ((data[p] * 299 + data[p + 1] * 587 + data[p + 2] * 114) / 1000 > 128) on[y * w + x] = 1;
      }
    }

    /* Rows group into bands. The creature is the topmost band plus anything
       separated from it by only a small gap (its legs). The wordmark sits far
       below and must never be touched. */
    var rowHas = new Uint8Array(h);
    for (y = 0; y < h; y++) for (x = 0; x < w; x++) if (on[y * w + x]) { rowHas[y] = 1; break; }
    var bands = [], start = -1;
    for (y = 0; y < h; y++) {
      if (rowHas[y] && start === -1) start = y;
      if (start !== -1 && !rowHas[y]) { bands.push([start, y - 1]); start = -1; }
    }
    if (start !== -1) bands.push([start, h - 1]);
    if (!bands.length) return false;

    var top = bands[0][0], bottom = bands[0][1];
    for (var b = 1; b < bands.length; b++) {
      var gap = bands[b][0] - bottom;
      if (gap > Math.max(8, (bottom - top) * 0.25)) break;
      bottom = bands[b][1];
    }

    var left = -1, right = -1;
    for (x = 0; x < w; x++) for (y = top; y <= bottom; y++) if (on[y * w + x]) { if (left === -1) left = x; right = x; break; }
    if (left === -1) return false;

    /* The art is anti-aliased, so the shortest run is noise. The most common
       run length across the creature is one cell. */
    var tally = {};
    for (y = top; y <= bottom; y++) {
      var runStart = left, cur = on[y * w + left];
      for (x = left + 1; x <= right + 1; x++) {
        var v = x > right ? -1 : on[y * w + x];
        if (v !== cur) {
          var len = x - runStart;
          if (runStart > left && x - 1 < right && len >= 3) tally[len] = (tally[len] || 0) + 1;
          runStart = x; cur = v;
        }
      }
    }
    var mode = 0, best = 0;
    for (var key in tally) if (tally[key] > best) { best = tally[key]; mode = parseInt(key, 10); }
    if (!mode) return false;

    var span = right - left + 1, tall = bottom - top + 1;
    var cols = Math.round(span / mode), rows = Math.round(tall / mode);
    if (cols < 5 || cols > 40 || rows < 5 || rows > 40) return false;
    var cw = span / cols, ch = tall / rows;
    if (Math.abs(cw - mode) / mode > 0.1) return false;

    var cells = new Uint8Array(cols * rows);
    for (r = 0; r < rows; r++) {
      for (c = 0; c < cols; c++) {
        var sx = Math.floor(left + (c + 0.5) * cw);
        var sy = Math.floor(top + (r + 0.5) * ch);
        cells[r * cols + c] = on[sy * w + sx];
      }
    }

    grid = { x: left, y: top, w: span, h: tall, cw: cw, ch: ch, cols: cols, rows: rows, cells: cells, natW: w };
    grid.units = flipUnits();
    return grid.units.length >= 4;
  }

  /* Which cells may move.
     Rows 0-1 (antennae) and the last row (legs) are frozen, so the creature
     keeps its reach. The outer columns are frozen, so it keeps its width.
     A cell only qualifies with three or more lit orthogonal neighbours, so a
     flip always opens a contained notch or fills a nook instead of growing a
     limb into empty space. Every unit is a mirrored pair, so whatever changes,
     the creature stays symmetrical - which is what makes it read as the same
     being on a different frequency rather than a different being. */
  function flipUnits() {
    var cells = grid.cells, cols = grid.cols, rows = grid.rows;
    function at(r, c) { return (r < 0 || c < 0 || r >= rows || c >= cols) ? 0 : cells[r * cols + c]; }
    var units = [];
    for (var r = 2; r <= rows - 2; r++) {
      for (var c = 1; c <= Math.floor((cols - 1) / 2); c++) {
        if (at(r - 1, c) + at(r + 1, c) + at(r, c - 1) + at(r, c + 1) < 3) continue;
        var mirror = cols - 1 - c;
        units.push(c === mirror ? [r * cols + c] : [r * cols + c, r * cols + mirror]);
      }
    }
    return units;
  }

  function buildVariants() {
    var rnd = mulberry(SEED);
    var take = Math.max(2, Math.round(grid.units.length * FLIP_RATIO));
    variants = [[]];
    for (var v = 1; v < VARIANT_COUNT; v++) {
      var pool = grid.units.slice(), pick = [];
      for (var k = 0; k < take && pool.length; k++) {
        pick = pick.concat(pool.splice(Math.floor(rnd() * pool.length), 1)[0]);
      }
      variants.push(pick);
    }
  }

  /* ---- geometry --------------------------------------------------------- */

  function layout() {
    if (!grid) return;
    var wr = win.getBoundingClientRect(), ir = img.getBoundingClientRect();
    if (!wr.height || !ir.width) return;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    canvas.style.left = (ir.left - wr.left) + 'px';
    canvas.style.top = (ir.top - wr.top) + 'px';
    canvas.style.width = ir.width + 'px';
    canvas.style.height = ir.height + 'px';
    canvas.width = Math.round(ir.width * dpr);
    canvas.height = Math.round(ir.height * dpr);
    grid.scale = (ir.width / grid.natW) * dpr;

    var centreNat = grid.y + grid.h / 2;
    var centreWin = (ir.top - wr.top) + centreNat * (ir.width / grid.natW);
    var pct = (centreWin / wr.height) * 100;
    targetProgress = Math.min(1, Math.max(0, (pct - 4) / 92));
  }

  function paint() {
    if (!grid || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    var flips = variants[variantIndex] || [];
    if (!flips.length) return;
    ctx.fillStyle = '#ffffff';
    var s = grid.scale;
    for (var i = 0; i < flips.length; i++) {
      var r = Math.floor(flips[i] / grid.cols), c = flips[i] % grid.cols;
      ctx.fillRect(Math.round((grid.x + c * grid.cw) * s), Math.round((grid.y + r * grid.ch) * s),
                   Math.ceil(grid.cw * s) + 1, Math.ceil(grid.ch * s) + 1);
    }
  }

  /* ---- the beat --------------------------------------------------------- */

  function scanProgress() {
    if (!line.getAnimations) return -1;
    var list = line.getAnimations();
    if (!list.length) return -1;
    var a = list[0];
    if (a.currentTime == null) return -1;
    var d = 5000;
    try { var cd = a.effect.getComputedTiming().duration; if (cd) d = cd; } catch (e) {}
    return (((a.currentTime % d) + d) % d) / d;
  }

  function crossed(a, b, t) {
    return b >= a ? (a < t && b >= t) : (a < t || b >= t);
  }

  function tick() {
    var p = scanProgress();
    if (p >= 0) {
      if (lastProgress >= 0 && crossed(lastProgress, p, targetProgress)) {
        passes++;
        if (passes % PASSES_PER_TUNE === 0) tune();
      }
      lastProgress = p;
    }
    rafId = window.requestAnimationFrame(tick);
  }

  function tune() {
    variantIndex = (variantIndex + 1) % variants.length;
    paint();
    window.dispatchEvent(new CustomEvent('pt:signal-tune', { detail: { variant: variantIndex } }));
  }

  function start() { if (!grid || live) return; live = true; lastProgress = -1; rafId = window.requestAnimationFrame(tick); }
  function stop() { if (live) { live = false; window.cancelAnimationFrame(rafId); } }

  /* Defensive on purpose. A phone backgrounds and restores this page
     constantly, and IntersectionObserver hands back BATCHED entries whose
     first record can be stale - reading entries[0] could leave the loop
     stopped forever once the hero scrolled out and back. Read the newest
     entry, and re-check on every signal that could have changed things. */
  function inView() {
    var r = win.getBoundingClientRect();
    return r.top < (window.innerHeight || 0) && r.bottom > 0;
  }

  function resume() {
    if (document.hidden || !inView()) { stop(); return; }
    start();
  }

  function watch() {
    if (!('IntersectionObserver' in window)) { start(); return; }
    new IntersectionObserver(function (entries) {
      var latest = entries[entries.length - 1];
      latest.isIntersecting ? start() : stop();
    }, { threshold: 0.15 }).observe(win);
    document.addEventListener('visibilitychange', resume);
    window.addEventListener('pageshow', resume);
    window.addEventListener('focus', resume);
    resume();
  }
}());